import Cookies from "js-cookie";
import React from "react";
import { invocaPhoneResponseSchema } from "./schema";
import { stripCountryCode } from "./formatPhone";
import { DEFAULT_AGENT_ID } from "../../state/features/hc/constants";
import type { HcState } from "../../state/features/hc/HcState";
import { getHcStorage } from "../../state/features/hc/useHcStorage";
import { HC_SESSION_COOKIE } from "../../state/features/hc/useHcCookies";
import { getSnowplowCookie } from "./snowplowCookie";

export interface InvocaPhone {
  /** Digits only, no +1, for tel: href. e.g. "8773750415" */
  phoneRaw: string;
  /** Human-readable display string from API. e.g. "877-375-0415" */
  phoneFormatted: string;
}

/**
 * How long to wait for the session identifiers to appear before sending the
 * campaign request without them.
 *
 * `useHc` establishes the session asynchronously — it waits for the Next router,
 * awaits a `CREATE_SESSION` mutation, and only then writes `hc` to `localStorage`
 * and sets the `healthcareSession` cookie. None of that exists yet when this hook
 * mounts on a cold first visit, so firing immediately produced Invoca records with
 * no `session_id` (PPI-1779).
 *
 * The wait is bounded on purpose. `DynamicPhone` shows a static fallback number
 * until the ring-pool number resolves, so every extra millisecond here is a
 * millisecond in which a click-to-call bypasses the ring pool entirely. This is
 * the knob to turn if session round trips prove slower than expected in the wild.
 */
export const SESSION_WAIT_TIMEOUT_MS = 2000;

/**
 * How much longer to look for the Snowplow cookie once the session has arrived.
 *
 * The cookie must NOT share the session's 2s budget. `usePageView` writes `_sp_id`
 * synchronously when it initializes the tracker, so on a page that runs Snowplow
 * the cookie is there on the first read and this window never opens. On a page
 * that does not — `dtc-v2` initializes no tracker, and the flag can be off — a
 * shared deadline would burn the full 2s every single time, even though the
 * session resolved on the first poll, and park a live click-to-call on the
 * fallback number for that whole stretch.
 *
 * Two extra polls is enough for effect-ordering jitter between `usePageView` and
 * this hook, and keeps the wait inside the "≤ 2 retries, ≤ 1s" bound that
 * `docs/guidelines.md` sets for delayed browser state.
 */
export const SNOWPLOW_GRACE_MS = 200;

/** How often to re-read storage and cookies while waiting for the identifiers. */
export const SESSION_POLL_INTERVAL_MS = 100;

function detectDevice(): "Desktop" | "Mobile" {
  /* istanbul ignore next */
  if (typeof navigator === "undefined") return "Desktop";
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
    ? "Mobile"
    : "Desktop";
}

/* istanbul ignore next */
function getRequestUrl(): string {
  return typeof window !== "undefined" ? window.location.href : "";
}

/**
 * Read the `hc` object from `localStorage`, tolerating both disabled storage and
 * malformed JSON. Callers get `null` and fall back to their own defaults.
 */
function readHcStorage(): HcState | null {
  try {
    return getHcStorage();
  } catch {
    // localStorage unavailable or `hc` is not parseable
    return null;
  }
}

function resolveAgentId(hc: HcState | null): string {
  /* istanbul ignore next */
  if (typeof window === "undefined") return DEFAULT_AGENT_ID;

  const queryAgentId = new URLSearchParams(window.location.search).get(
    "agent_id"
  );
  if (queryAgentId) return queryAgentId;

  return hc?.agent_id || DEFAULT_AGENT_ID;
}

/**
 * Resolve the Healthcare session ID using the same order as `useHc`: the `hc`
 * storage object first, then the `healthcareSession` cookie. Invoca reporting
 * keys off this value, so an empty string leaves the call record unattributed.
 */
function resolveSessionId(hc: HcState | null): string {
  return hc?.healthcareSession || Cookies.get(HC_SESSION_COOKIE) || "";
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Everything the campaign payload needs that is not available synchronously. */
interface SessionIdentifiers {
  /** The `hc` object, or `null` when storage is unavailable or unparseable. */
  hc: HcState | null;
  /** Raw Snowplow ID cookie, or `null` if it never appeared. */
  snowplowCookie: string | null;
}

/**
 * Read `hc` from storage and the Snowplow cookie, on two separate budgets: the
 * session gets {@link SESSION_WAIT_TIMEOUT_MS}, the cookie gets
 * {@link SNOWPLOW_GRACE_MS} afterwards and never past the session deadline. See
 * both for why each bound is the size it is.
 *
 * Returning visitors already have both on the very first read and never poll, so
 * this costs them nothing.
 *
 * The loop deliberately waits for `hc.healthcareSession` rather than settling for
 * the cookie. `useHc` sets the cookie synchronously alongside its Redux dispatch,
 * but the `hc` object is written to `localStorage` a render later — exiting on the
 * cookie would consistently fire one tick early and lose `user_id`, `sub_id` and
 * `location`. The cookie remains the fallback once the deadline expires, via
 * `resolveSessionId`.
 *
 * Whatever has appeared by then is what gets sent. A missing Snowplow cookie costs
 * `domain_userid` / `domain_sessionid` on that one record; it must never cost the
 * caller a ring-pool number, and must not delay one either.
 */
async function waitForIdentifiers(
  signal: AbortSignal
): Promise<SessionIdentifiers> {
  const deadline = Date.now() + SESSION_WAIT_TIMEOUT_MS;
  let hc = readHcStorage();
  let snowplowCookie = getSnowplowCookie();

  // The session owns the full budget. The cookie is re-read on every poll so one
  // that lands during this wait is picked up for free.
  while (!hc?.healthcareSession && !signal.aborted && Date.now() < deadline) {
    await delay(SESSION_POLL_INTERVAL_MS);
    hc = readHcStorage();
    snowplowCookie = getSnowplowCookie();
  }

  // Then a short grace window for the cookie alone, never past the session
  // deadline — if the session already burned the budget, do not extend it.
  const cookieDeadline = Math.min(deadline, Date.now() + SNOWPLOW_GRACE_MS);
  while (!snowplowCookie && !signal.aborted && Date.now() < cookieDeadline) {
    await delay(SESSION_POLL_INTERVAL_MS);
    snowplowCookie = getSnowplowCookie();
  }

  return { hc, snowplowCookie };
}

function resolveSite(): string {
  /* istanbul ignore next */
  if (typeof window === "undefined") return "enroll.pivothealth.com";
  return window.location.hostname === "www.pivothealth.com"
    ? "www.pivothealth.com"
    : "enroll.pivothealth.com";
}

/**
 * Fetches a dynamic ring-pool phone number from the Invoca campaign API.
 * Returns null while loading and on any failure.
 *
 * Waits for the Healthcare session and the Snowplow cookie to be established
 * before sending the request, so the campaign record is attributable — bounded by
 * {@link SESSION_WAIT_TIMEOUT_MS}, after which it sends without them rather than
 * leaving the caller on the fallback number indefinitely.
 *
 * Sends exactly one request per mount. Uses AbortController to cancel both the
 * session wait and the in-flight request on unmount.
 */
export function useInvocaPhone(): InvocaPhone | null {
  const [phone, setPhone] = React.useState<InvocaPhone | null>(null);

  React.useEffect(() => {
    const url = process.env["NEXT_PUBLIC_SEM_CONTENT_URL"];
    if (!url) return;

    const controller = new AbortController();

    const run = async () => {
      try {
        const { hc, snowplowCookie } = await waitForIdentifiers(
          controller.signal
        );
        if (controller.signal.aborted) return;

        const response = await fetch(`${url}/call/campaign`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Omitted rather than sent empty — SEM Content treats the header's
            // presence as "a Snowplow session exists to parse".
            ...(snowplowCookie && { "snowplow-data": snowplowCookie }),
          },
          signal: controller.signal,
          body: JSON.stringify({
            product: "STM",
            src: "",
            publisher_click_id: "",
            session_id: resolveSessionId(hc),
            user_id: hc?.healthcareUserId ?? "",
            sub_id: hc?.subid ?? 0,
            experiment_id: "",
            variation_id: "",
            device: detectDevice(),
            placement: "IN_HEADER",
            state: hc?.location?.state_acronym ?? "",
            zip: hc?.location?.zip_code ?? "",
            request_url: getRequestUrl(),
            site: resolveSite(),
            agent_id: resolveAgentId(hc),
            profile: {
              qle: null,
              family_size: 0,
              fpl: null,
              tobacco: null,
              gender: "m",
              age: 0,
            },
            call_type: "bundle",
          }),
        });

        if (!response.ok) return;

        const raw: unknown = await response.json();
        const parsed = invocaPhoneResponseSchema.safeParse(raw);

        if (parsed.success) {
          const { promo_number, promo_number_formatted } =
            parsed.data.data.phone;
          setPhone({
            phoneRaw: stripCountryCode(promo_number),
            phoneFormatted: promo_number_formatted,
          });
        }
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") return;
        // Swallow all other errors — component renders null on failure.
      }
    };

    void run();
    return () => controller.abort();
  }, []);

  return phone;
}
